<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <router-link :to="backUrl" class="btn btn-outline-secondary btn-sm">
          <vue-feather type="arrow-left" size="14" class="me-1" />
          Back
        </router-link>
        <router-link :to="`/app/tests/${testId}/questions`" class="btn btn-outline-primary btn-sm">
          <vue-feather type="help-circle" size="14" class="me-1" />
          Manage Questions
        </router-link>
      </template>
    </breadcrumb-index>

    <div class="card">
      <div class="card-header">
        <h6 class="card-title mb-0">Edit Test</h6>
      </div>
      <div class="card-body">
        <form @submit.prevent="handleSubmit">
          <div class="row">
            <div class="col-lg-8">
              <div class="mb-3">
                <label class="form-label">Test Title <span class="text-danger">*</span></label>
                <input type="text" class="form-control" v-model="form.title" required />
              </div>

              <div class="mb-3">
                <label class="form-label">Description</label>
                <textarea class="form-control" v-model="form.description" rows="3"></textarea>
              </div>

              <div class="row">
                <div class="col-md-4">
                  <div class="mb-3">
                    <label class="form-label">Duration (minutes) <span class="text-danger">*</span></label>
                    <input type="number" class="form-control" v-model="form.duration" min="1" required />
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="mb-3">
                    <label class="form-label">Total Marks <span class="text-danger">*</span></label>
                    <input type="number" class="form-control" v-model="form.totalMarks" min="1" required />
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="mb-3">
                    <label class="form-label">Passing Marks <span class="text-danger">*</span></label>
                    <input type="number" class="form-control" v-model="form.passingMarks" min="0" required />
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Negative Marking</label>
                    <input type="number" class="form-control" v-model="form.negativeMarking" min="0" step="0.25" />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Status</label>
                    <select class="form-select" v-model="form.status">
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <div class="form-check">
                      <input type="checkbox" class="form-check-input" id="shuffleQuestions" v-model="form.shuffleQuestions" />
                      <label class="form-check-label" for="shuffleQuestions">Shuffle Questions</label>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Show Answers</label>
                    <select class="form-select" v-model="form.showAnswers">
                      <option value="immediately">Immediately after submission</option>
                      <option value="after_submit">After test ends</option>
                      <option value="never">Never</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="d-flex gap-2 pt-2">
                <button type="submit" class="btn btn-primary" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
                  Update Test
                </button>
                <router-link :to="backUrl" class="btn btn-outline-secondary">Cancel</router-link>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/services/api'
import { useAlert } from '@/plugins/sweetalert'

export default {
  name: 'EditTest',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const alert = useAlert()
    const testId = route.params.id

    const loading = ref(false)
    const testData = ref(null)
    const form = ref({
      title: '',
      description: '',
      duration: 30,
      totalMarks: 100,
      passingMarks: 40,
      negativeMarking: 0,
      shuffleQuestions: false,
      showAnswers: 'after_submit',
      status: 'draft',
      scheduledAt: '',
      lessonId: null,
      seriesId: null,
      topicId: null
    })

    const backUrl = computed(() => {
      if (form.value.lessonId) {
        return `/app/lessons/${form.value.lessonId}/tests`
      }
      if (form.value.seriesId) {
        return `/app/test-series/${form.value.seriesId}/tests`
      }
      if (form.value.topicId) {
        return `/app/topics/${form.value.topicId}/tests`
      }
      return '/app/dashboard'
    })

    const breadcrumbs = computed(() => {
      const items = [
        { title: 'Dashboard', url: '/app/dashboard' },
        { title: 'Exams', url: '/app/exams/list' }
      ]
      
      if (form.value.lessonId) {
        // Exam → Subject → Topic → Lesson → Test (edit)
        if (testData.value?.examId) {
          items.push({ title: testData.value?.examName || 'Exam', url: `/app/exams/${testData.value.examId}/subjects` })
        }
        if (testData.value?.subjectId) {
          items.push({ title: testData.value?.subjectName || 'Subject', url: `/app/subjects/${testData.value.subjectId}/topics` })
        }
        if (testData.value?.topicId) {
          items.push({ title: testData.value?.topicName || 'Topic', url: `/app/topics/${testData.value.topicId}/lessons` })
        }
        items.push({ title: testData.value?.lessonTitle || 'Lesson', url: `/app/lessons/${form.value.lessonId}/tests` })
      } else if (form.value.seriesId) {
        // Exam → TestSeries → Test (edit)
        if (testData.value?.examId) {
          items.push({ title: testData.value?.examName || 'Exam', url: `/app/exams/${testData.value.examId}/test-series` })
        }
        items.push({ title: testData.value?.seriesName || 'Test Series', url: `/app/test-series/${form.value.seriesId}/tests` })
      } else if (form.value.topicId) {
        // Exam → Subject → Topic → Test (edit)
        if (testData.value?.examId) {
          items.push({ title: testData.value?.examName || 'Exam', url: `/app/exams/${testData.value.examId}/subjects` })
        }
        if (testData.value?.subjectId) {
          items.push({ title: testData.value?.subjectName || 'Subject', url: `/app/subjects/${testData.value.subjectId}/topics` })
        }
        items.push({ title: testData.value?.topicName || 'Topic', url: `/app/topics/${form.value.topicId}/tests` })
      }
      items.push({ title: form.value.title || 'Edit Test', active: true })
      return items
    })

    const fetchData = async () => {
      try {
        const res = await api.get(`/v1/admin/tests/${testId}`)
        const test = res.data.test
        testData.value = test
        form.value = {
          title: test.title,
          description: test.description || '',
          duration: test.duration,
          totalMarks: test.totalMarks,
          passingMarks: test.passingMarks,
          negativeMarking: test.negativeMarking || 0,
          shuffleQuestions: test.shuffleQuestions || false,
          showAnswers: test.showAnswers || 'after_submit',
          status: test.status || 'draft',
          scheduledAt: test.scheduledAt ? test.scheduledAt.slice(0, 16) : '',
          lessonId: test.lessonId,
          seriesId: test.seriesId,
          topicId: test.topicId
        }
      } catch (err) {
        alert.error('Error', 'Failed to load test')
      }
    }

    const handleSubmit = async () => {
      loading.value = true
      try {
        await api.put(`/v1/admin/tests/${testId}`, { test: form.value })
        alert.success('Success', 'Test updated successfully')
        router.push(backUrl.value)
      } catch (err) {
        alert.error('Error', err.response?.data?.message || 'Failed to update test')
      } finally {
        loading.value = false
      }
    }

    onMounted(fetchData)

    return { testId, form, loading, breadcrumbs, backUrl, handleSubmit }
  }
}
</script>
